import React, { useState } from 'react';
import { ExperimentType, ReactionType, ChemExperimentState } from './types';
import { KetcherModal } from './KetcherModal';
import { Edit2, FileText } from 'lucide-react';
import { OpenOrderContext } from '../chatGPT/ChatBot';
import { useContext } from 'react';

interface ReactionSetupTabProps {
  state: ChemExperimentState;
  onUpdate: (updates: Partial<ChemExperimentState>) => void;
}

const EXPERIMENT_TYPES: ExperimentType[] = ['API', 'FD', 'ADL'];

const REACTION_TYPES: ReactionType[] = [
  'Peptide',
  'Enzyme',
  'FlowChemistry',
  'Lyophilisation',
  'Chromatography',
  'PhiTec',
  'Polymorphism',
  'Premix',
  'SprayDrying',
  'TSU',
];

/**
 * ReactionSetupTab Component
 * 
 * Allows users to configure basic reaction parameters:
 * - Experiment type (API/FD/ADL)
 * - Reaction type (various chemistry types)
 * - Batch ID and reaction name
 * - Structure editor placeholder (future integration point)
 */
export const ReactionSetupTab: React.FC<ReactionSetupTabProps> = ({ state, onUpdate }) => {
  const [structureData, setStructureData] = useState<{ smiles: string; molfile: string }>({
    smiles: state.metadata?.structureSmiles || '',
    molfile: state.metadata?.structureMolfile || '',
  });
  const { OpnedOrder }: any = useContext(OpenOrderContext);
  const [isKetcherOpen, setIsKetcherOpen] = useState(false);

  const handleSave = () => {
    const dataToSave = {
      ...state,
      metadata: {
        ...state.metadata,
        structureSmiles: structureData.smiles,
        structureMolfile: structureData.molfile,
      },
    };
    onUpdate({
      metadata: dataToSave.metadata,
    });
    alert('Setup saved!');
  };

  const handleStructureSave = (smiles: string, molfile: string) => {
    setStructureData({ smiles, molfile });
    // Also update state immediately
    onUpdate({
      metadata: {
        ...state.metadata,
        structureSmiles: smiles,
        structureMolfile: molfile,
      },
    });
  };

  const handleImportStructure = () => {
    const importValue = prompt(
      'Paste SMILES string or MOLfile content:\n\n' +
      'For SMILES: e.g., CCO (ethanol)\n' +
      'For MOLfile: Paste the full MOLfile text'
    );

    if (importValue && importValue.trim()) {
      // Detect format
      const isMolfile = importValue.includes('M  END') || importValue.split('\n').length > 5;

      if (isMolfile) {
        setStructureData({ smiles: '', molfile: importValue.trim() });
      } else {
        setStructureData({ smiles: importValue.trim(), molfile: '' });
      }

      onUpdate({
        metadata: {
          ...state.metadata,
          structureSmiles: isMolfile ? '' : importValue.trim(),
          structureMolfile: isMolfile ? importValue.trim() : '',
        },
      });

      alert('Structure imported successfully!');
    }
  };

  return (
    <div style={styles.container}>
      <h3 style={styles.heading}>Reaction Setup</h3>

      {/* Experiment Type */}
      <div style={styles.formGroup}>
        <label style={styles.label}>
          Experiment Type <span style={styles.required}>*</span>
        </label>
        <select
          style={styles.select}
          value={state.experimentType}
          onChange={(e) => onUpdate({ experimentType: e.target.value as ExperimentType })}
        >
          {EXPERIMENT_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
        <span style={styles.hint}>API = Active Pharmaceutical Ingredient, FD = Final Drug, ADL = Analytical</span>
      </div>

      {/* Reaction Type */}
      <div style={styles.formGroup}>
        <label style={styles.label}>
          Reaction Type <span style={styles.required}>*</span>
        </label>
        <select
          style={styles.select}
          value={state.reactionType}
          onChange={(e) => onUpdate({ reactionType: e.target.value as ReactionType })}
        >
          {REACTION_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>

      {/* Batch ID */}
      <div style={styles.formGroup}>
        <label style={styles.label}>
          Batch ID <span style={styles.required}>*</span>
        </label>
        <input
          type="text"
          style={styles.input}
          value={OpnedOrder.batchid}
          // onChange={(e) => onUpdate({ batchId: e.target.value })}
          placeholder="e.g., BATCH-2024-001"
        />
      </div>

      {/* Reaction Name */}
      <div style={styles.formGroup}>
        <label style={styles.label}>Reaction Name</label>
        <input
          type="text"
          style={styles.input}
          value={state.reactionName || ''}
          onChange={(e) => onUpdate({ reactionName: e.target.value })}
          placeholder="e.g., Amide Coupling Step 1"
        />
      </div>

      {/* Chemical Structure Section */}
      <div style={styles.formGroup}>
        <div style={styles.structureHeader}>
          <label style={styles.label}>Chemical Structure</label>
          <div style={styles.structureActions}>
            <button
              style={styles.importButton}
              onClick={handleImportStructure}
              title="Import structure"
            >
              <FileText size={14} />
              <span>Import</span>
            </button>
            <button
              style={styles.editButton}
              onClick={() => setIsKetcherOpen(true)}
              title="Edit structure"
            >
              <Edit2 size={14} />
              <span>{structureData.smiles || structureData.molfile ? 'Edit' : 'Create'}</span>
            </button>
          </div>
        </div>

        {/* Structure Display */}
        {structureData.smiles || structureData.molfile ? (
          <div style={styles.structureDisplay}>
            <div style={styles.structurePreview}>
              <div style={styles.structureIcon}>
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{ color: '#2563eb' }}
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
              </div>
              <div style={styles.structureDetails}>
                {structureData.smiles && (
                  <div style={styles.structureInfo}>
                    <span style={styles.structureLabel}>SMILES:</span>
                    <code style={styles.structureCode}>{structureData.smiles}</code>
                  </div>
                )}
                {structureData.molfile && !structureData.smiles && (
                  <div style={styles.structureInfo}>
                    <span style={styles.structureLabel}>MOLfile:</span>
                    <span style={styles.structureCode}>
                      {structureData.molfile.split('\n')[0]}... ({structureData.molfile.split('\n').length} lines)
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div style={styles.structurePlaceholder}>
            <p style={styles.placeholderText}>No structure defined</p>
            <p style={styles.placeholderSubtext}>
              Click "Create" to draw a structure or "Import" to load from SMILES/MOLfile
            </p>
          </div>
        )}
      </div>

      {/* Ketcher Modal */}
      <KetcherModal
        isOpen={isKetcherOpen}
        onClose={() => setIsKetcherOpen(false)}
        onSave={handleStructureSave}
        initialStructure={structureData.molfile || structureData.smiles}
      />

      {/* Save Button */}
      <div style={styles.buttonGroup}>
        <button style={styles.saveButton} onClick={handleSave}>
          Save Setup
        </button>
      </div>

      {/* Current State Preview (Dev Helper) */}
      <details style={styles.details}>
        <summary style={styles.summary}>View Current State (Debug)</summary>
        <pre style={styles.pre}>{JSON.stringify(state, null, 2)}</pre>
      </details>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '20px',
    height: '100%',
    overflowY: 'auto',
  },
  heading: {
    margin: '0 0 20px 0',
    fontSize: '18px',
    fontWeight: 600,
    color: '#1e293b',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#334155',
  },
  required: {
    color: '#ef4444',
  },
  select: {
    width: '100%',
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    outline: 'none',
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  hint: {
    display: 'block',
    marginTop: '4px',
    fontSize: '12px',
    color: '#64748b',
  },
  structureHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  structureActions: {
    display: 'flex',
    gap: '6px',
  },
  importButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#2563eb',
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  editButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#fff',
    backgroundColor: '#2563eb',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  structureDisplay: {
    marginTop: '8px',
    padding: '12px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
  },
  structurePreview: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  structureIcon: {
    flexShrink: 0,
  },
  structureDetails: {
    flex: 1,
    minWidth: 0,
  },
  structureInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  },
  structureLabel: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#475569',
    whiteSpace: 'nowrap',
  },
  structureCode: {
    fontSize: '12px',
    color: '#1e293b',
    backgroundColor: '#fff',
    padding: '4px 8px',
    borderRadius: '4px',
    fontFamily: 'monospace',
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
  },
  structurePlaceholder: {
    marginTop: '8px',
    padding: '24px',
    backgroundColor: '#f8fafc',
    border: '2px dashed #cbd5e1',
    borderRadius: '6px',
    textAlign: 'center',
  },
  placeholderText: {
    margin: '0 0 4px 0',
    fontSize: '14px',
    fontWeight: 500,
    color: '#64748b',
  },
  placeholderSubtext: {
    margin: 0,
    fontSize: '12px',
    color: '#94a3b8',
  },
  buttonGroup: {
    display: 'flex',
    gap: '10px',
    marginTop: '24px',
  },
  saveButton: {
    flex: 1,
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#fff',
    backgroundColor: '#2563eb',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  details: {
    marginTop: '24px',
    padding: '12px',
    backgroundColor: '#f1f5f9',
    borderRadius: '6px',
    fontSize: '12px',
  },
  summary: {
    cursor: 'pointer',
    fontWeight: 500,
    color: '#475569',
  },
  pre: {
    marginTop: '8px',
    padding: '8px',
    backgroundColor: '#fff',
    borderRadius: '4px',
    overflow: 'auto',
    fontSize: '11px',
  },
};

